import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from "react-redux";
import PegaWebEmbed from "./PegaWebEmbed";
// Removed unused import: revertToDefaults
import { toggleFullscreen, closeSidePanel, minimizeSidePanel, addToEmbedHistory, navigateEmbedHistoryBack, navigateEmbedHistoryForward, setCurrentAddress, setCaseResolverInitialized, clearAccessGroupInfo } from '../features/sidePanelSlice';
import { setEmployeePegaWebEmbedProps, setSidePanelTitle } from '../features/channelSlice';
import { getCaseResolver, resetCaseResolver } from '../utils/pegaCaseResolver';

// Pick readable text (dark or white) for a given background colour by luminance.
// Handles #rgb, #rrggbb, and rgb()/rgba() so any brand colour resolves.
const readableTextOn = (color) => {
  if (typeof color !== 'string') return '#ffffff';
  let r, g, b;
  const c = color.trim();
  let hex = c.replace('#', '');
  if (/^[0-9a-f]{3}$/i.test(hex)) hex = hex.split('').map((ch) => ch + ch).join('');
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    const n = parseInt(hex, 16);
    r = (n >> 16) & 255; g = (n >> 8) & 255; b = n & 255;
  } else {
    const m = c.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (!m) return '#ffffff';
    r = +m[1]; g = +m[2]; b = +m[3];
  }
  // Perceived luminance (0–255). Above ~150 → dark text reads better.
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum > 150 ? '#16202e' : '#ffffff';
};

// True on Windows — used to swap the macOS traffic-light controls for
// Windows-style minimize / maximize / close buttons.
// Dev/test override: localStorage 'forceWindowsChrome' = 'true' | 'false'
// forces the value (reload after setting, since this is read once at load).
const IS_WINDOWS = (() => {
  if (typeof window !== 'undefined') {
    const forced = window.localStorage.getItem('forceWindowsChrome');
    if (forced === 'true') return true;
    if (forced === 'false') return false;
  }
  return typeof navigator !== 'undefined'
    && /Windows|Win32|Win64|WinNT/i.test(
      (navigator.userAgentData && navigator.userAgentData.platform) || navigator.userAgent || navigator.platform || ''
    );
})();

export default function SidePanel({ expanded, isMobile = false }) {
  const dispatch = useDispatch();
  const [hasBeenExpanded, setHasBeenExpanded] = useState(false);
  const [embedKey, setEmbedKey] = useState(0);
  const [embedReady, setEmbedReady] = useState(true);
  const [addressBarValue, setAddressBarValue] = useState('Home');
  const [isAddressBarFocused, setIsAddressBarFocused] = useState(false);
  const [addressBarError, setAddressBarError] = useState(null);
  const [addressOnFocus, setAddressOnFocus] = useState(null); // Track original value when focused
  const [errorFading, setErrorFading] = useState(false); // Track error fade-out state
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');
  const paneRef = useRef(null);
  // const iframeRef = useRef(null); // Unused - Ref for the embed iframe (for refresh/back/forward navigation)
  const hasReceivedWebSocketPayloadRef = useRef(false); // Track if WebSocket payload ever arrived
  const isNavigatingHistoryRef = useRef(false); // Track if we're navigating through history (to prevent duplicate history entries)
  const lastBuiltConfigIdRef = useRef(null); // Track which configId props were last built for

  // Get employee pane config from top-level config
  const employeePaneSrc = useSelector((state) => state.channel.employeePaneSrc);
  const employeePegaWebEmbedProps = useSelector((state) => state.channel.employeePegaWebEmbedProps);
  const webEmbedConfig = useSelector((state) => state.channel.webEmbedConfig); // Base config (credentials, URLs)
  const webEmbedBranding = useSelector((state) => state.channel.webEmbedBranding); // Brand theme only
  const sidePanelTitle = useSelector((state) => state.channel.sidePanelTitle) || 'Employee Panel';
  // Brand header (fake Pega-portal header above the embed): primary brand colour
  // + brand logo. logoUrl is promoted from the active brand by channelSlice.
  const brandHeaderColour = useSelector((state) => state.channel.gradientColor1) || '#1C2837';
  const brandHeaderLogo = useSelector((state) => state.channel.logoUrl) || null;
  // Header title font: reuse the embed theme's H1 font-family (brand JSON
  // webEmbed.theme.components.text.h1) so the fake header matches the portal's
  // headings. Falls back to the panel default when not specified.
  const headerTitleFont =
    webEmbedBranding?.theme?.components?.text?.h1?.['font-family']
    || webEmbedConfig?.theme?.components?.text?.h1?.['font-family']
    || undefined;
  const hostServer = useSelector((state) => state.channel.hostServer);
  const applicationAlias = useSelector((state) => state.channel.applicationAlias);
  const isFullscreen = useSelector((state) => state.sidePanel.isFullscreen);
  
  // History tracking
  const embedPropsHistory = useSelector((state) => state.sidePanel.embedPropsHistory);
  const currentHistoryIndex = useSelector((state) => state.sidePanel.currentHistoryIndex);
  const canGoBack = currentHistoryIndex > 0;
  const canGoForward = currentHistoryIndex < embedPropsHistory.length - 1;
  
  // Address bar and case resolver state
  const currentAddress = useSelector((state) => state.sidePanel.currentAddress);
  const configId = useSelector((state) => state.channel.configId);
  const caseResolverInitialized = useSelector((state) => state.sidePanel.caseResolverInitialized);
  const pegaEnabled = useSelector((state) => state.channel.pegaEnabled);
  const liveModalOpen = useSelector((state) => state.live?.isLiveModalOpen); // Track LiveModal state
  
  // DEBUG: Log infrastructure values from Redux (commented out to reduce console noise)
  // console.log('🔍 SidePanel Redux infrastructure values:', {
  //   hostServer,
  //   applicationAlias,
  //   hasWebEmbedConfig: !!webEmbedConfig,
  //   hasEmployeePegaWebEmbedProps: !!employeePegaWebEmbedProps
  // });

  // Track when panel has been expanded at least once
  useEffect(() => {
    if (expanded && !hasBeenExpanded) {
      console.log('🚀 Panel expanding for FIRST TIME');
      setHasBeenExpanded(true);
      
      // CRITICAL FIX: On first panel expansion, if we have config but no WebSocket payload,
      // we should immediately build props from config instead of waiting
      const hasConfig = webEmbedConfig || webEmbedBranding;
      const hasWebSocketPayload = employeePegaWebEmbedProps && Object.keys(employeePegaWebEmbedProps).length > 0;
      
      console.log('🔍 First panel open check:', {
        hasConfig,
        hasWebSocketPayload,
        hasReceivedWebSocketPayloadRef: hasReceivedWebSocketPayloadRef.current,
        webEmbedConfig: !!webEmbedConfig,
        webEmbedBranding: !!webEmbedBranding,
        employeePegaWebEmbedProps: !!employeePegaWebEmbedProps
      });
      
      if (hasConfig && !hasWebSocketPayload && !hasReceivedWebSocketPayloadRef.current) {
        console.log('🚀 First panel open with config but no WebSocket payload - immediately building from config');
        // Build props synchronously right here to ensure they're ready before render
        const hasThemeObject = webEmbedBranding?.theme || webEmbedConfig?.theme;
        
        const pegawebembed = {
          pegaServerUrl: hostServer || process.env.REACT_APP_PEGA_BASE_URL || process.env.REACT_APP_API_BASE_URL,
          pageClass: webEmbedConfig?.pageClass || "Data-Portal",
          pageID: webEmbedConfig?.pageID || "pyHome",
          action: webEmbedConfig?.action || "openPage",
          container: 'iframe',
          ...(hasThemeObject 
            ? { theme: webEmbedBranding?.theme || webEmbedConfig?.theme }
            : {}
          ),
        };
        
        console.log('✅ Built props on first panel open:', {
          pageID: pegawebembed.pageID,
          hasTheme: !!pegawebembed.theme
        });
        
        dispatch(setEmployeePegaWebEmbedProps(pegawebembed));
        // Add to history for back/forward navigation (with current title)
        dispatch(addToEmbedHistory({ props: pegawebembed, title: sidePanelTitle }));
        setEmbedKey(prev => prev + 1);
      }
    }
  }, [expanded, hasBeenExpanded, webEmbedConfig, webEmbedBranding, employeePegaWebEmbedProps, hostServer, applicationAlias, dispatch, sidePanelTitle]);

  // Rebuild PegaWebEmbed props whenever webEmbedConfig changes (ensure fresh config is always used)
  useEffect(() => {
    console.log('🔄 SidePanel useEffect triggered:', {
      hasBeenExpanded,
      hasWebEmbedConfig: !!webEmbedConfig,
      hasWebEmbedBranding: !!webEmbedBranding,
      webEmbedBrandingKeys: webEmbedBranding ? Object.keys(webEmbedBranding) : null,
      webEmbedBrandingTheme: webEmbedBranding?.theme ? JSON.stringify(webEmbedBranding.theme) : null,
      hasEmployeePaneSrc: !!employeePaneSrc,
      hasEmployeePegaWebEmbedProps: !!employeePegaWebEmbedProps,
      employeePegaWebEmbedPropsPageID: employeePegaWebEmbedProps?.pageID
    });
    
    // CRITICAL FIX: Don't use hardcoded fallback credentials on initial load!
    // The problem was that the effect ran before config.json was loaded,
    // causing it to use hardcoded credentials (5ECF8F39A32ADF3DADA12733EDC61E4A)
    // instead of the correct ones from config (D203AB40325D3674C97443AA5EECA0EE).
    // Solution: Skip the effect if config isn't loaded yet, and wait for it to load.
    const isConfigLoaded = !!webEmbedConfig || !!employeePaneSrc;
    const hasExistingProps = employeePegaWebEmbedProps && Object.keys(employeePegaWebEmbedProps).length > 0;
    
    // Only skip if config is NOT loaded AND no existing props
    // If config IS loaded, we MUST build props immediately, don't wait for panel to expand
    if (!isConfigLoaded && !hasExistingProps) {
      console.log('⏳ Config not loaded yet - skipping effect to avoid using hardcoded fallback credentials');
      return; // Wait for config.json to load before building props
    }
    
    // IMPORTANT: DO NOT guard with hasBeenExpanded check here!
    // This was causing a race condition where events with payloads would trigger panel expansion,
    // but the payload wouldn't be applied because hasBeenExpanded was still false.
    // The effect must run immediately when config/branding changes, regardless of panel state.
    
    // IMPORTANT: If employeePegaWebEmbedProps is already set (from WebSocket), respect it and don't rebuild!
    // This preserves case/assignment-specific data sent from WebSocket messages.
    // EXCEPTION: If webEmbedBranding has changed, we MUST rebuild to apply the new theme!
    const hasExistingWebSocketPayload = employeePegaWebEmbedProps && Object.keys(employeePegaWebEmbedProps).length > 0;
    
    // Check if the current theme in props matches the theme in webEmbedBranding
    const currentThemeInProps = employeePegaWebEmbedProps?.theme;
    const newThemeFromBranding = webEmbedBranding?.theme || webEmbedConfig?.theme;
    const themeHasChanged = JSON.stringify(currentThemeInProps) !== JSON.stringify(newThemeFromBranding);
    
    const isNewConfig = lastBuiltConfigIdRef.current !== null && lastBuiltConfigIdRef.current !== configId;

    if (hasExistingWebSocketPayload && !themeHasChanged && !isNewConfig) {
      // Mark that we've received a WebSocket payload (even if it's cleared later, don't auto-rebuild)
      hasReceivedWebSocketPayloadRef.current = true;

      console.log('✅ WebSocket payload detected, preserving:', {
        pageID: employeePegaWebEmbedProps.pageID,
        action: employeePegaWebEmbedProps.action,
        allKeys: Object.keys(employeePegaWebEmbedProps)
      });
      // Skip rebuilding - use the WebSocket payload as-is
      return;
    }
    
    if (hasExistingWebSocketPayload && themeHasChanged) {
      console.log('🎨 Theme has changed - rebuilding props to apply new theme:', {
        oldThemeKeys: currentThemeInProps ? Object.keys(currentThemeInProps) : null,
        newThemeKeys: newThemeFromBranding ? Object.keys(newThemeFromBranding) : null,
        pageID: employeePegaWebEmbedProps.pageID
      });
    }
    
    // Rebuild props if we have config OR branding (PegaWebEmbed always takes priority over employeePaneSrc URL)
    if (webEmbedConfig || webEmbedBranding) {
      // Always rebuild from config when webEmbedConfig OR webEmbedBranding is available
      // This runs whenever webEmbedConfig or webEmbedBranding changes, ensuring we use the latest config/theme
      
      // Determine theme settings: theme object takes priority over themeID
      const hasThemeObject = webEmbedBranding?.theme || webEmbedConfig?.theme;
      
      console.log('🎨 Theme selection logic:', {
        hasWebEmbedBrandingTheme: !!webEmbedBranding?.theme,
        hasWebEmbedConfigTheme: !!webEmbedConfig?.theme,
        webEmbedBrandingTheme: webEmbedBranding?.theme,
        webEmbedConfigTheme: webEmbedConfig?.theme,
        willUseTheme: hasThemeObject,
        selectedTheme: webEmbedBranding?.theme || webEmbedConfig?.theme,
        webEmbedBrandingFull: webEmbedBranding,
        webEmbedConfigFull: webEmbedConfig
      });
      
      const pegawebembed = {
        // Infrastructure settings from base config (sidePanel section) or fallback defaults
        pegaServerUrl: hostServer || (process.env.REACT_APP_PEGA_BASE_URL || process.env.REACT_APP_API_BASE_URL) + '/api/v1/pega/prweb',
        pageClass: webEmbedConfig?.pageClass || "Data-Portal",
        pageID: webEmbedConfig?.pageID || "pyHome",
        action: webEmbedConfig?.action || "openPage",
        // IMPORTANT: Wrap in iframe container to isolate from AppView's PegaWebEmbed
        // Per Pega docs: Multiple web embeds on same page require iframe isolation for all but one
        container: 'iframe',
        // Theme: Always use theme object if available (brand takes priority over config)
        ...(hasThemeObject 
          ? { theme: webEmbedBranding?.theme || webEmbedConfig?.theme }
          : {}
        ),
      };
      
      console.log('✅ Built PegaWebEmbed props from config:', {
        clientId: pegawebembed.clientId,
        clientSecret: pegawebembed.clientSecret?.substring(0, 8) + '...',
        tokenUri: pegawebembed.tokenUri,
        hasTheme: !!pegawebembed.theme,
        theme: pegawebembed.theme,
        themeType: typeof pegawebembed.theme,
        themeKeys: pegawebembed.theme ? Object.keys(pegawebembed.theme) : null,
        themeJSON: pegawebembed.theme ? JSON.stringify(pegawebembed.theme).substring(0, 200) : null,
        source: 'webEmbedConfig (sidePanel from config.json)'
      });
      
      console.log('📦 Full PegaWebEmbed props object:', pegawebembed);
      console.log('🔍 Spread test - hasThemeObject:', hasThemeObject);
      console.log('🔍 Spread test - theme from spread:', { theme: webEmbedBranding?.theme || webEmbedConfig?.theme });
      
      lastBuiltConfigIdRef.current = configId;
      if (isNewConfig) {
        setEmbedReady(false);
        dispatch(setEmployeePegaWebEmbedProps(null)); // clear stale props from previous brand before setting fresh ones
      }
      dispatch(setEmployeePegaWebEmbedProps(pegawebembed));
      // Add to history for back/forward navigation (with current title)
      dispatch(addToEmbedHistory({ props: pegawebembed, title: sidePanelTitle }));
      // Increment key to force remount
      setEmbedKey(prev => prev + 1);
    }
    // REMOVED: The fallback branch that used hardcoded credentials
    // Problem: It was using wrong clientSecret (5ECF8F39A32ADF3DADA12733EDC61E4A) on initial load
    // Solution: Now we skip rendering until config.json loads with correct credentials
    // NOTE: Intentionally NOT including hostServer or applicationAlias in dependencies
    // - hostServer/applicationAlias: these are infrastructure constants that should never change after initial load
    // We DO include employeePegaWebEmbedProps so the effect can detect WebSocket payloads and preserve them
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBeenExpanded, webEmbedConfig, webEmbedBranding, employeePaneSrc, employeePegaWebEmbedProps, dispatch]);

  // Track changes to employeePegaWebEmbedProps and add to history
  // This captures WebSocket updates that change the embed (e.g., new caseID, pageID)
  useEffect(() => {
    if (!employeePegaWebEmbedProps || Object.keys(employeePegaWebEmbedProps).length === 0) {
      // No props yet, nothing to track
      return;
    }

    // Skip adding to history if we're currently navigating through history
    // This prevents duplicate entries when using back/forward buttons
    if (isNavigatingHistoryRef.current) {
      console.log('⏭️ Skipping history add - currently navigating through history');
      isNavigatingHistoryRef.current = false; // Reset flag
      return;
    }

    // Add current props to history whenever they change
    // The addToEmbedHistory action will automatically skip duplicates
    console.log('📚 employeePegaWebEmbedProps changed, adding to history:', {
      pageID: employeePegaWebEmbedProps.pageID,
      caseID: employeePegaWebEmbedProps.caseID,
      action: employeePegaWebEmbedProps.action,
      title: sidePanelTitle,
      allKeys: Object.keys(employeePegaWebEmbedProps)
    });
    dispatch(addToEmbedHistory({ props: employeePegaWebEmbedProps, title: sidePanelTitle }));
  }, [employeePegaWebEmbedProps, sidePanelTitle, dispatch]);

  // Initialize case resolver when config changes - fetch case types from Pega.
  // Skipped entirely for non-Pega configs (no /casetypes call).
  useEffect(() => {
    if (!configId || !pegaEnabled) return;

    const resolver = getCaseResolver();
    resolver.setConfigId(configId);
    
    // Initialize resolver by fetching case types from /casetypes API
    const initializeResolver = async () => {
      try {
        console.log('🔄 Initializing case resolver for config:', configId);
        const success = await resolver.initialize(configId);
        
        if (success) {
          dispatch(setCaseResolverInitialized(true));
          const status = resolver.getStatus();
          console.log('✅ Case resolver initialized:', status);
        } else {
          dispatch(setCaseResolverInitialized(false));
          console.warn('⚠️ Case resolver initialization failed - user must enter full case key');
        }
      } catch (error) {
        console.error('❌ Case resolver initialization error:', error);
        dispatch(setCaseResolverInitialized(false));
      }
    };
    
    initializeResolver();
  }, [configId, pegaEnabled, dispatch]);

  // Reset case resolver when config changes
  useEffect(() => {
    return () => {
      // Cleanup on unmount or config change
      dispatch(clearAccessGroupInfo());
      resetCaseResolver();
    };
  }, [configId, dispatch]);

  // Release embed gate once the case resolver has warmed up the backend's Pega token cache.
  // Falls back to rendering after 3s in case case resolver fails, so the embed is never permanently hidden.
  useEffect(() => {
    if (caseResolverInitialized) {
      setEmbedReady(true);
      return;
    }
    if (!embedReady) {
      const fallback = setTimeout(() => setEmbedReady(true), 3000);
      return () => clearTimeout(fallback);
    }
  }, [caseResolverInitialized, embedReady]);

  // Auto-collapse side panel when LiveModal opens
  useEffect(() => {
    if (liveModalOpen && expanded) {
      console.log('📺 LiveModal opened, collapsing side panel');
      dispatch(closeSidePanel());
    }
  }, [liveModalOpen, expanded, dispatch]);

  // Auto-clear error after a few seconds with fade effect
  useEffect(() => {
    if (addressBarError && !errorFading) {
      const fadeTimer = setTimeout(() => {
        setErrorFading(true);
      }, 3000); // Start fading after 3 seconds
      
      const clearTimer = setTimeout(() => {
        setAddressBarError(null);
        setErrorFading(false);
      }, 3500); // Clear completely after 3.5 seconds
      
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [addressBarError, errorFading]);

  // Sync address bar with currentAddress from Redux
  // Also clear any error state when address changes successfully
  useEffect(() => {
    if (!isAddressBarFocused) {
      setAddressBarValue(currentAddress || 'Home');
      // Clear error state when address changes (successful navigation elsewhere)
      setAddressBarError(null);
      setErrorFading(false);
    }
  }, [currentAddress, isAddressBarFocused]);

  // Update address bar when employeePegaWebEmbedProps changes (from WebSocket)
  // IMPORTANT: This effect should only update address bar based on EXPLICIT case/assignment actions
  // It should NOT update when navigating to Home (which clears caseID from props)
  useEffect(() => {
    if (!employeePegaWebEmbedProps) return;
    
    // Extract case/assignment ID from props
    const caseID = employeePegaWebEmbedProps.caseID || employeePegaWebEmbedProps.caseId;
    const assignmentID = employeePegaWebEmbedProps.assignmentID || employeePegaWebEmbedProps.assignmentId;
    const pageID = employeePegaWebEmbedProps.pageID;
    const action = employeePegaWebEmbedProps.action;
    
    // Check if this is a Home navigation (action=openPage with pageID=pyHome and NO caseID)
    const isHomeNavigation = (action === 'openPage' && (pageID === 'pyHome' || pageID === 'Home') && !caseID && !assignmentID);
    
    if (isHomeNavigation) {
      console.log('🏠 Props indicate Home navigation - setting address to Home');
      dispatch(setCurrentAddress('Home'));
      return;
    }
    
    // Only update address bar if we have an explicit case/assignment action
    if (action === 'openCase' && caseID) {
      // Extract just the pyID from the full pzInsKey (e.g., "LABB-WORK-SERVICE MA-12345" -> "MA-12345")
      const pyID = caseID.includes(' ') ? caseID.split(' ').slice(1).join(' ') : caseID;
      console.log('📂 Props indicate case opened:', pyID.toUpperCase());
      dispatch(setCurrentAddress(pyID.toUpperCase()));
    } else if (action === 'openAssignment' && assignmentID) {
      // Extract just the pyID from assignment ID if it's a full key
      const pyID = assignmentID.includes(' ') ? assignmentID.split(' ').slice(1).join(' ') : assignmentID;
      console.log('📋 Props indicate assignment opened:', pyID.toUpperCase());
      dispatch(setCurrentAddress(pyID.toUpperCase()));
    }
    // For other actions (like openPage with a caseID but action=openPage from WebSocket), 
    // let the PegaWebEmbed event handlers update the address bar instead
  }, [employeePegaWebEmbedProps, dispatch]);

  // Determine what to render: PRIORITY ORDER:
  // 1. PegaWebEmbed if webEmbedConfig OR webEmbedBranding exists (side panel config from database)
  // 2. iframe if employeePaneSrc exists (legacy URL mode)
  // 3. Loading state if config not loaded yet (CRITICAL FIX!)
  // When Pega is disabled for this config, never use the pega-web-embed branch —
  // fall through to a plain iframe (from sidePanel.url) so stale webEmbed state
  // can't leave the panel blank.
  const embedType = (pegaEnabled && (webEmbedConfig || webEmbedBranding))
    ? 'pega-web-embed'
    : (employeePaneSrc ? 'iframe' : 'loading');

  // State to track case resolution in progress
  const [isResolvingCase, setIsResolvingCase] = useState(false);

  // Handle going home - defined first so it can be used in handleAddressBarSubmit
  const handleGoHome = useCallback(() => {
    // Clear any address bar error when navigating home
    setAddressBarError(null);
    
    if (embedType === 'pega-web-embed' && (webEmbedConfig || webEmbedBranding)) {
      const hasThemeObject = webEmbedBranding?.theme || webEmbedConfig?.theme;
      
      const pegawebembed = {
        pegaServerUrl: hostServer || (process.env.REACT_APP_PEGA_BASE_URL || process.env.REACT_APP_API_BASE_URL) + '/api/v1/pega/prweb',
        pageClass: webEmbedConfig?.pageClass || "Data-Portal",
        pageID: webEmbedConfig?.pageID || "pyHome",
        action: webEmbedConfig?.action || "openPage",
        container: 'iframe',
        ...(hasThemeObject 
          ? { theme: webEmbedBranding?.theme || webEmbedConfig?.theme }
          : {}
        ),
      };
      
      console.log('🏠 Home: Reloading PegaWebEmbed with default config');
      dispatch(setEmployeePegaWebEmbedProps(pegawebembed));
      dispatch(setCurrentAddress('Home'));
      setEmbedKey(prev => prev + 1);
    } else {
      const defaultUrl = employeePaneSrc || `${hostServer || 'https://example.com'}/portal`;
      console.log('🏠 Home: Switching to iframe mode with default URL:', defaultUrl);
      dispatch(setEmployeePegaWebEmbedProps(null));
      dispatch({ type: 'channel/setEmployeePaneSrc', payload: defaultUrl });
      dispatch(setCurrentAddress('Home'));
    }
  }, [embedType, webEmbedConfig, webEmbedBranding, hostServer, employeePaneSrc, dispatch]);

  // Handle address bar submission (on blur or Enter key)
  const handleAddressBarSubmit = useCallback(async (value) => {
    const trimmedValue = value.trim().toUpperCase(); // Always uppercase
    
    // Prevent refresh if value hasn't changed from when we focused (case-insensitive comparison)
    if (addressOnFocus !== null && trimmedValue === addressOnFocus.trim().toUpperCase()) {
      console.log('📍 Address bar unchanged (case-insensitive), skipping refresh');
      // Just update to uppercase display without triggering any action
      setAddressBarValue(addressOnFocus.trim().toUpperCase() || 'Home');
      setAddressOnFocus(null);
      return;
    }
    setAddressOnFocus(null);
    
    // Clear any previous error
    setAddressBarError(null);
    setErrorFading(false);
    
    // Update address bar to uppercase immediately
    setAddressBarValue(trimmedValue || 'Home');
    
    if (!trimmedValue || trimmedValue.toLowerCase() === 'home') {
      // Navigate to home
      handleGoHome();
      dispatch(setCurrentAddress('Home'));
      return;
    }

    // Check if we're using web embed
    if (embedType !== 'pega-web-embed') {
      console.log('📍 Address bar: Not using web embed, ignoring input');
      return;
    }

    // Show loading state
    setIsResolvingCase(true);
    console.log('📍 Address bar: Resolving case ID:', trimmedValue);
    
    try {
      const resolver = getCaseResolver();
      
      // Try to initialize on-demand if not ready
      if (!resolver.isInitialized && configId) {
        console.log('📍 Address bar: Initializing case resolver on-demand...');
        const initSuccess = await resolver.initialize(configId);
        if (!initSuccess) {
          setAddressBarError('Unable to initialize case resolver. Check your connection.');
          console.warn('⚠️ Address bar: On-demand initialization failed');
          return;
        }
        dispatch(setCaseResolverInitialized(true));
      }
      
      // Use findValidCase to validate via API and try all case types
      const result = await resolver.findValidCase(trimmedValue);
      
      if (result.error) {
        // Case not found or access denied after trying all possibilities
        setAddressBarError(result.error);
        console.warn('⚠️ Address bar: Case resolution failed:', result.error);
        return;
      }

      console.log('✅ Address bar: Case validated:', result);
      
      // Build PegaWebEmbed props to open the case/assignment in full mode with assignment header
      // Refer to: https://docs.pega.com/bundle/platform/page/platform/user-experience/attributes-web-embed.html
      const hasThemeObject = webEmbedBranding?.theme || webEmbedConfig?.theme;
      
      // Get user's locale from browser (e.g., "en_US", "en_GB", "fr_FR")
      // Pega uses underscore format (e.g., 'fr_CA'), not hyphen format
      const browserLocale = navigator.language || navigator.userLanguage || 'en-US';
      const pegaLocale = browserLocale.replace('-', '_'); // Convert "en-US" to "en_US"
      
      // Detect if this is an assignment ID (work class contains "ASSIGN")
      const isAssignment = result.pxObjClass && result.pxObjClass.toUpperCase().includes('ASSIGN');
      
      const pegawebembed = {
        pegaServerUrl: hostServer || (process.env.REACT_APP_PEGA_BASE_URL || process.env.REACT_APP_API_BASE_URL) + '/api/v1/pega/prweb',
        action: isAssignment ? 'openAssignment' : 'openCase',
        ...(isAssignment 
          ? { assignmentID: result.pzInsKey } // Full uppercase pzInsKey for assignment
          : { caseID: result.pzInsKey }       // Full uppercase pzInsKey for case
        ),
        container: 'iframe',
        // casePage: 'full' - Uses the full Case Page when displaying case-related UI
        // Other options: 'assignment', 'assignmentWithStages', 'simplifiedAssignment'
        casePage: 'full',
        // assignmentHeader: Show the assignment header with case info, status, actions (default: true)
        // IMPORTANT: Must be 'true' (string) for both cases AND assignments
        assignmentHeader: 'true',
        // locale: User's locale for proper date/number formatting and translations (e.g., 'fr_CA')
        locale: pegaLocale,
        ...(hasThemeObject 
          ? { theme: webEmbedBranding?.theme || webEmbedConfig?.theme }
          : {}
        ),
      };

      console.log(`📍 Address bar: Opening validated ${isAssignment ? 'assignment' : 'case'} with props:`, pegawebembed);
      dispatch(setEmployeePegaWebEmbedProps(pegawebembed));
      // Don't change the side panel header title - keep it as configured
      dispatch(setCurrentAddress(result.pyID)); // Just the ID in address bar
      setEmbedKey(prev => prev + 1);
      
    } catch (error) {
      console.error('❌ Address bar: Error during case resolution:', error);
      setAddressBarError('An error occurred. Please try again.');
    } finally {
      setIsResolvingCase(false);
    }
    
  }, [embedType, webEmbedBranding, webEmbedConfig, hostServer, dispatch, handleGoHome, addressOnFocus, configId]);
  
  // CRITICAL: Only render PegaWebEmbed if we actually have the config
  // This prevents using hardcoded fallback credentials on first load
  const shouldRenderPegaWebEmbed = pegaEnabled && embedType === 'pega-web-embed' && (webEmbedConfig || employeePegaWebEmbedProps);

  console.log('🎯 SidePanel embedType decision:', {
    embedType,
    hasWebEmbedConfig: !!webEmbedConfig,
    hasWebEmbedBranding: !!webEmbedBranding,
    hasEmployeePaneSrc: !!employeePaneSrc,
    hasEmployeePegaWebEmbedProps: !!employeePegaWebEmbedProps,
    shouldRenderPegaWebEmbed,
    hasBeenExpanded,
    expanded,
    shouldShowContent: isFullscreen || expanded
  });

  // Validate that critical props are available (keep error logging)
  if (employeePegaWebEmbedProps && !employeePegaWebEmbedProps.pegaServerUrl) {
    console.error('❌ PegaWebEmbed: Missing pegaServerUrl! Check that hostServer is set in config.json', {
      embedProps: employeePegaWebEmbedProps,
      hostServer,
      applicationAlias,
      configStatus: 'loaded'
    });
  }

  // Handler functions for macOS window controls
  const handleClose = () => {
    // Ensure fullscreen is also turned off when closing
    dispatch(closeSidePanel());
  };

  const handleMinimize = () => {
    // Minimize should exit fullscreen mode but keep the side panel expanded in normal mode
    if (isFullscreen) {
      dispatch(toggleFullscreen()); // Just exit fullscreen, don't collapse
    } else {
      dispatch(minimizeSidePanel()); // Minimize if not in fullscreen
    }
  };

  const handleFullscreen = () => {
    dispatch(toggleFullscreen());
  };

  // Browser navigation handlers - navigate through embed props history
  const handleBack = () => {
    if (canGoBack) {
      dispatch(navigateEmbedHistoryBack());
      // Set flag to prevent adding duplicate to history
      isNavigatingHistoryRef.current = true;
      // Load the previous props and title from history
      const previousEntry = embedPropsHistory[currentHistoryIndex - 1];
      if (previousEntry) {
        console.log('⬅️ Loading previous embed props:', previousEntry.props, 'Title:', previousEntry.title);
        dispatch(setEmployeePegaWebEmbedProps(previousEntry.props));
        // Restore the title if it was saved
        if (previousEntry.title) {
          dispatch(setSidePanelTitle(previousEntry.title));
        }
        setEmbedKey(prev => prev + 1); // Force remount with new props
      }
    } else {
      console.log('⚠️ No previous history available');
    }
  };

  const handleForward = () => {
    if (canGoForward) {
      dispatch(navigateEmbedHistoryForward());
      // Set flag to prevent adding duplicate to history
      isNavigatingHistoryRef.current = true;
      // Load the next props and title from history
      const nextEntry = embedPropsHistory[currentHistoryIndex + 1];
      if (nextEntry) {
        console.log('➡️ Loading next embed props:', nextEntry.props, 'Title:', nextEntry.title);
        dispatch(setEmployeePegaWebEmbedProps(nextEntry.props));
        // Restore the title if it was saved
        if (nextEntry.title) {
          dispatch(setSidePanelTitle(nextEntry.title));
        }
        setEmbedKey(prev => prev + 1); // Force remount with new props
      }
    } else {
      console.log('⚠️ No forward history available');
    }
  };

  const handleRefresh = () => {
    // Simply remount the current embed
    console.log('🔄 Refreshing current embed');
    setEmbedKey(prev => prev + 1);
  };

  // When in fullscreen mode, we should always show content regardless of expanded prop
  // When not in fullscreen, respect the expanded prop from parent
  const shouldShowContent = isFullscreen || expanded;

  return (
    <div
      ref={paneRef}
      className="h-full cara-chrome"
      style={{
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        width: isFullscreen ? '100vw' : '100%',
        height: isFullscreen ? '100vh' : '100%',
        overflow: 'hidden',
        background: 'var(--cara-shell-bg)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        zIndex: isFullscreen ? 8000 : 50,
        pointerEvents: shouldShowContent ? 'auto' : 'none', // Ensure pointer events work when content should show
      }}
    >
      {/* Title bar - Chrome-style for desktop, mobile-friendly for mobile.
        * Always reserve the iOS safe-area inset at the top so the system status
        * bar (notch / Dynamic Island / iPad status overlay) doesn't cover the
        * window controls. env() resolves to 0 on macOS/desktop browsers, so
        * this is a no-op everywhere except iOS. Important for the
        * iPad-landscape-desktop trial where isMobile is false but we're still
        * inside iOS Safari. */}
      <div style={{
        height: isMobile
          ? 'calc(60px + env(safe-area-inset-top, 0px))'
          : 'calc(32px + env(safe-area-inset-top, 0px))',
        boxSizing: 'border-box',
        background: 'var(--cara-chrome-bg)',
        borderBottom: '1px solid var(--cara-chrome-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '0 16px' : '0 8px',
        position: 'sticky',
        top: 0,
        zIndex: 120,
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '8px', order: (IS_WINDOWS && !isMobile) ? 3 : 0 }}>
          {isMobile ? (
            // Mobile: Back + Forward buttons on left
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button 
                onClick={handleBack}
                disabled={!canGoBack}
                style={{ 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  background: canGoBack ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  border: 'none',
                  cursor: canGoBack ? 'pointer' : 'not-allowed',
                  transition: 'background 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: canGoBack ? '#bdc1c6' : '#5f6368',
                  opacity: canGoBack ? 1 : 0.4,
                }}
                onMouseEnter={(e) => {
                  if (canGoBack) e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  if (canGoBack) e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                title={canGoBack ? "Back" : "No previous history"}
              >
                ←
              </button>
              <button 
                onClick={handleForward}
                disabled={!canGoForward}
                style={{ 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '50%', 
                  background: canGoForward ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  border: 'none',
                  cursor: canGoForward ? 'pointer' : 'not-allowed',
                  transition: 'background 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  color: canGoForward ? '#bdc1c6' : '#5f6368',
                  opacity: canGoForward ? 1 : 0.4,
                }}
                onMouseEnter={(e) => {
                  if (canGoForward) e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  if (canGoForward) e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                title={canGoForward ? "Forward" : "No forward history"}
              >
                →
              </button>
              <button
                onClick={handleRefresh}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  color: '#bdc1c6',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'; }}
                title="Refresh"
              >
                ↻
              </button>
            </div>
          ) : IS_WINDOWS ? (
            // Desktop (Windows): minimize / maximize / close controls
            <div style={{ display: 'flex', gap: '2px' }}>
              <button
                onClick={handleClose}
                className="chrome-window-control"
                title="Minimize"
                style={{ width: '38px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: 'var(--cara-text)', cursor: 'pointer', borderRadius: '4px', padding: 0, transition: 'background 0.15s, color 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(127,127,127,0.18)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: '13px', lineHeight: 1 }}>&#x2212;</span>
              </button>
              <button
                onClick={handleFullscreen}
                className="chrome-window-control"
                title={isFullscreen ? 'Restore' : 'Maximize'}
                style={{ width: '38px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: 'var(--cara-text)', cursor: 'pointer', borderRadius: '4px', padding: 0, transition: 'background 0.15s, color 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(127,127,127,0.18)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: '11px', lineHeight: 1 }}>{isFullscreen ? '❐' : '□'}</span>
              </button>
              <button
                onClick={handleClose}
                className="chrome-window-control"
                title="Close"
                style={{ width: '38px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: 'var(--cara-text)', cursor: 'pointer', borderRadius: '4px', padding: 0, transition: 'background 0.15s, color 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#e81123'; e.currentTarget.firstChild.style.color = '#ffffff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.firstChild.style.color = 'var(--cara-text)'; }}
              >
                <span style={{ fontSize: '12px', lineHeight: 1 }}>&#x2715;</span>
              </button>
            </div>
          ) : (
            // Desktop: macOS window controls
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleClose}
                className="chrome-window-control"
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#ff5f57',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.8';
                  const icon = e.target.querySelector('.window-icon');
                  if (icon) icon.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                  const icon = e.target.querySelector('.window-icon');
                  if (icon) icon.style.opacity = '0';
                }}
                title="Close"
              >
                <span 
                  className="window-icon"
                  style={{ 
                    fontSize: '8px', 
                    color: '#4d0000',
                    opacity: '0',
                    transition: 'opacity 0.2s',
                    lineHeight: '1',
                  }}
                >✕</span>
              </button>
            <button
              onClick={handleMinimize}
              className="chrome-window-control"
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#ffbd2e',
                border: 'none',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.8';
                const icon = e.target.querySelector('.window-icon');
                if (icon) icon.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
                const icon = e.target.querySelector('.window-icon');
                if (icon) icon.style.opacity = '0';
              }}
              title="Minimize"
            >
              <span 
                className="window-icon"
                style={{ 
                  fontSize: '8px', 
                  color: '#4d3300',
                  opacity: '0',
                  transition: 'opacity 0.2s',
                  lineHeight: '1',
                }}
              >−</span>
            </button>
              <button
                onClick={handleFullscreen}
                className="chrome-window-control"
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: '#28ca42',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.8';
                  const icon = e.target.querySelector('.window-icon');
                  if (icon) icon.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                  const icon = e.target.querySelector('.window-icon');
                  if (icon) icon.style.opacity = '0';
                }}
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                <span 
                  className="window-icon"
                  style={{ 
                    fontSize: '6px', 
                    color: '#002d00',
                    opacity: '0',
                    transition: 'opacity 0.2s',
                    lineHeight: '1',
                  }}
                >{isFullscreen ? '⤓' : '⤢'}</span>
              </button>
            </div>
          )}
        </div>
        {isMobile && mobileSearchOpen ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '0 8px', gap: '6px' }}>
            <input
              autoFocus
              type="text"
              value={mobileSearchValue}
              onChange={(e) => setMobileSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const value = mobileSearchValue.trim();
                  if (value) {
                    handleAddressBarSubmit(value);
                  }
                  setMobileSearchOpen(false);
                } else if (e.key === 'Escape') {
                  setMobileSearchOpen(false);
                }
              }}
              placeholder="Enter Case ID"
              className="mobile-case-search-input"
              style={{
                flex: 1,
                height: '32px',
                background: 'var(--cara-input-bg)',
                border: (addressBarError && !errorFading) ? '1px solid #ff5252' : '1px solid #3c4043',
                borderRadius: '16px',
                color: '#e8eaed',
                padding: '0 12px',
                fontSize: '14px',
                outline: 'none',
                textTransform: 'uppercase',
              }}
            />
            <style>{`
              .mobile-case-search-input::placeholder { text-transform: none; }
            `}</style>
            {isResolvingCase && (
              <span className="material-icons" style={{ fontSize: '18px', color: '#bdc1c6', animation: 'spin 1s linear infinite' }}>autorenew</span>
            )}
          </div>
        ) : (
          <span style={{
            fontSize: isMobile ? '14px' : '13px',
            color: 'var(--cara-text)',
            fontWeight: 400,
            flex: 1,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            padding: '0 8px',
          }}>{sidePanelTitle}</span>
        )}
        {isMobile ? (
          // Mobile: Search + Home + Close buttons on right
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => {
                setMobileSearchValue('');
                setAddressBarError(null);
                setErrorFading(false);
                setMobileSearchOpen((prev) => !prev);
              }}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: mobileSearchOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#bdc1c6',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = mobileSearchOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'; }}
              title="Search case ID"
            >
              <span
                className="material-icons"
                style={{ fontSize: '20px', color: '#bdc1c6', lineHeight: '1' }}
              >search</span>
            </button>
            <button 
              onClick={handleGoHome}
              style={{ 
                width: '36px', 
                height: '36px', 
                borderRadius: '50%', 
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              title="Home"
            >
              <span 
                className="material-icons"
                style={{ 
                  fontSize: '20px', 
                  color: '#bdc1c6',
                  lineHeight: '1',
                }}
              >home</span>
            </button>
            <button 
              onClick={handleClose}
              style={{ 
                width: '36px', 
                height: '36px', 
                borderRadius: '50%', 
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              title="Close"
            >
              <span 
                className="material-icons"
                style={{ 
                  fontSize: '20px', 
                  color: '#ffffff',
                  lineHeight: '1',
                }}
              >close</span>
            </button>
          </div>
        ) : (
          <div style={{ width: (IS_WINDOWS && !isMobile) ? '0px' : '60px' }}></div>
        )}
      </div>



      {/* Navigation bar - Chrome-style for desktop, hidden for mobile */}
      {!isMobile && (
        <div style={{
          height: '48px',
          background: 'var(--cara-chrome-2-bg)',
          borderBottom: '1px solid var(--cara-chrome-border)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          gap: '8px',
          position: 'sticky',
          top: '32px',
          zIndex: 118,
        }}>
        {/* Navigation buttons */}
        <button 
          onClick={handleBack}
          disabled={!canGoBack}
          style={{
          background: 'none',
          border: 'none',
          color: canGoBack ? '#bdc1c6' : '#5f6368',
          fontSize: '20px',
          cursor: canGoBack ? 'pointer' : 'not-allowed',
          padding: '8px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s',
          opacity: canGoBack ? 1 : 0.4,
        }}
          onMouseEnter={(e) => canGoBack && (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)')}
          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
          title={canGoBack ? "Back" : "No previous history"}
        >
          ←
        </button>
        <button 
          onClick={handleForward}
          disabled={!canGoForward}
          style={{
          background: 'none',
          border: 'none',
          color: canGoForward ? '#bdc1c6' : '#5f6368',
          fontSize: '20px',
          cursor: canGoForward ? 'pointer' : 'not-allowed',
          padding: '8px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s',
          opacity: canGoForward ? 1 : 0.4,
        }}
          onMouseEnter={(e) => canGoForward && (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)')}
          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
          title={canGoForward ? "Forward" : "No forward history"}
        >
          →
        </button>
        <button 
          onClick={handleRefresh}
          style={{
            background: 'none',
            border: 'none',
            color: '#bdc1c6',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
          title="Refresh"
        >
          ↻
        </button>
        <button 
          style={{
            background: 'none',
            border: 'none',
            color: '#bdc1c6',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleGoHome}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
          </svg>
        </button>

        {/* Address bar */}
        <div style={{
          flex: 1,
          background: 'var(--cara-input-bg)',
          border: (addressBarError && !errorFading) ? '1px solid #ff5252' : (errorFading ? '1px solid rgba(255, 82, 82, 0)' : (isAddressBarFocused ? '1px solid #8ab4f8' : '1px solid #3c4043')),
          borderRadius: '24px',
          padding: '6px 12px',
          margin: '0 8px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          transition: 'border-color 0.5s',
        }}>
          <span style={{ color: embedType === 'pega-web-embed' ? '#34a853' : '#9aa0a6', fontSize: '14px' }}>
            {embedType === 'pega-web-embed' ? '🔒' : '🌐'}
          </span>
          <input 
            type="text" 
            value={addressBarValue}
            onChange={(e) => setAddressBarValue(e.target.value)}
            onFocus={(e) => {
              setIsAddressBarFocused(true);
              setAddressBarError(null);
              setErrorFading(false);
              setAddressOnFocus(e.target.value); // Store original value
            }}
            onBlur={(e) => {
              setIsAddressBarFocused(false);
              const currentValue = e.target.value.trim();
              const uppercaseValue = currentValue.toUpperCase() || 'Home';
              
              // Check if value actually changed (case-insensitive) before submitting
              const originalValue = addressOnFocus?.trim().toUpperCase() || '';
              if (uppercaseValue === originalValue) {
                // Only case changed, just update display without triggering action
                setAddressBarValue(uppercaseValue);
                setAddressOnFocus(null);
                return;
              }
              
              // Value actually changed, update display and submit
              setAddressBarValue(uppercaseValue);
              handleAddressBarSubmit(currentValue);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.target.blur(); // This will trigger onBlur which calls handleAddressBarSubmit
              } else if (e.key === 'Escape') {
                setAddressBarValue(currentAddress || 'Home');
                setAddressOnFocus(null);
                e.target.blur();
              }
            }}
            placeholder={embedType === 'pega-web-embed' ? 'Enter case ID (e.g., HM-12345) or Home' : 'URL'}
            disabled={isResolvingCase}
            style={{
              background: 'none',
              border: 'none',
              outline: 'none',
              flex: 1,
              fontSize: '14px',
              color: isResolvingCase ? '#9aa0a6' : '#e8eaed',
              fontFamily: 'inherit',
              opacity: isResolvingCase ? 0.7 : 1,
            }}
          />
          {isResolvingCase && (
            <span 
              className="material-icons"
              style={{ 
                color: '#8ab4f8', 
                fontSize: '16px',
                animation: 'spin 1s linear infinite',
              }}
            >
              refresh
            </span>
          )}
          {addressBarError && !isResolvingCase && (
            <span 
              className="material-icons"
              style={{ 
                color: '#ff5252', 
                fontSize: '16px',
                cursor: 'pointer',
                opacity: errorFading ? 0 : 1,
                transition: 'opacity 0.5s',
              }}
              onClick={() => setAddressBarError(null)}
              title={addressBarError}
            >
              ⚠️
            </span>
          )}
        </div>

        {/* Chrome menu */}
        <button style={{
          background: 'none',
          border: 'none',
          color: '#bdc1c6',
          fontSize: '20px',
          cursor: 'pointer',
          padding: '8px',
          borderRadius: '4px',
        }}>
          ⋮
        </button>
        </div>
      )}

      {/* Fake Pega-portal header — brand-coloured strip above the embed, the same
        * width as the iframe, sitting between the chrome/nav bar and the content.
        * Carries the brand logo (top-left) and the panel title, mimicking the
        * portal masthead the embed itself doesn't render. Shown on both desktop
        * and mobile (sticks below the chrome — 80px desktop, 60px title bar mobile). */}
      {(
        <div style={{
          height: '48px',
          flexShrink: 0,
          background: brandHeaderColour,
          borderBottom: '1px solid rgba(0,0,0,0.15)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '0 16px',
          position: 'sticky',
          top: isMobile ? 'calc(60px + env(safe-area-inset-top, 0px))' : '80px',
          zIndex: 117,
        }}>
          {brandHeaderLogo && (
            <img
              src={brandHeaderLogo}
              alt="Brand"
              style={{ height: '26px', maxWidth: '120px', objectFit: 'contain', filter: 'brightness(1.05)' }}
            />
          )}
          <span style={{
            color: readableTextOn(brandHeaderColour),
            fontSize: '15px',
            fontWeight: 600,
            letterSpacing: '0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textShadow: readableTextOn(brandHeaderColour) === '#ffffff' ? '0 1px 2px rgba(0,0,0,0.25)' : 'none',
            fontFamily: headerTitleFont,
          }}>
            {sidePanelTitle}
          </span>
        </div>
      )}

      {/* Browser content area */}
      <div style={{
        flex: 1,
        background: '#ffffff',
        position: 'relative',
        height: isMobile
          ? 'calc(100vh - 60px - 48px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))'
          : 'calc(100% - 128px)', // Mobile: title bar (60) + brand header (48); Desktop: title bar (32) + nav bar (48) + brand header (48)
        overflow: 'hidden',
      }}>
        {/* Iframe */}
        <div className="w-full h-full" style={{
          display: embedType === 'iframe' && shouldShowContent ? 'block' : 'none',
          height: '100%',
        }}>
          <iframe
            src={employeePaneSrc}
            className="w-full h-full"
            title="Employee View"
            sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
            style={{ 
              border: 'none',
              borderRadius: '0',
              background: '#ffffff',
            }}
          />
        </div>

        {/* Pega Web Embed */}
        <div 
          className="w-full h-full overflow-y-auto" 
          style={{
            display: embedType === 'pega-web-embed' && shouldShowContent ? 'block' : 'none',
            height: '100%',
            background: '#ffffff',
            // Hide scrollbar for mobile-like experience
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
          }}
        >
          <style>{`
            /* Hide scrollbar for Chrome, Safari and Opera in SidePanel */
            .w-full.h-full.overflow-y-auto::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {shouldRenderPegaWebEmbed && expanded && embedReady && (
            <PegaWebEmbed
              key={`pegaembed-${embedKey}`}
              {...employeePegaWebEmbedProps}
            />
          )}
        </div>

        {/* Loading indicator when content should not show */}
        {!shouldShowContent && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: '#bdc1c6',
            fontSize: '14px',
          }}>
            <div style={{ marginBottom: '8px', fontSize: '24px' }}>🌐</div>
            Browser ready
          </div>
        )}
        
        {/* Loading indicator when config is not loaded */}
        {embedType === 'loading' && shouldShowContent && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: '#bdc1c6',
            fontSize: '14px',
          }}>
            <div style={{ marginBottom: '8px', fontSize: '24px' }}>⏳</div>
            Loading configuration...
          </div>
        )}
      </div>
    </div>
  );
}
